import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'

const TURBO_STREAM = 'text/vnd.turbo-stream.html'

export default class extends Controller {
  static targets = ['input', 'status']
  static classes = ['error']

  static values = {
    url: String,
    field: String,
    missingSurface: String,
    requestFailed: String,
    invalidValue: String
  }

  #pending = false
  #queuedValue = null
  #mutex = null
  #acceptedValue = null

  connect () {
    this.#acceptedValue = this.inputTarget.value
    this.inputTarget.addEventListener('change', this.#onInputChange)
  }

  disconnect () {
    this.inputTarget.removeEventListener('change', this.#onInputChange)
  }

  #onInputChange = (event) => {
    event.stopPropagation()

    const value = parseInt(this.inputTarget.value, 10)
    if (Number.isNaN(value) || !this.inputTarget.checkValidity()) {
      this.#rejectInput()
      return
    }

    if (this.#pending) {
      this.#queuedValue = value
      return
    }
    if (this.#mutexNode()?.getAttribute('aria-busy') === 'true') return

    this.#clearStatus()
    this.#request({ [this.fieldValue]: value })
  }

  #request (body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      this.#showError(this.requestFailedValue)
      return
    }

    this.#mutex = this.#mutexNode()
    this.#mutex?.setAttribute('aria-busy', 'true')
    this.#pending = true
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    fetch(this.urlValue, {
      method: 'PATCH',
      signal: controller.signal,
      headers: {
        Accept: TURBO_STREAM,
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(body)
    })
      .then(async (response) => {
        if (response.status === 422) {
          this.#pending = false
          this.#queuedValue = null
          this.#showError(this.missingSurfaceValue)
          return
        }

        if (response.status === 400) {
          this.#pending = false
          this.#queuedValue = null
          this.#rejectInput()
          return
        }

        if (!response.ok) throw new Error(`Request failed: ${response.status}`)

        this.#acceptedValue = String(body[this.fieldValue])

        if (this.#queuedValue !== null) {
          const queued = this.#queuedValue
          this.#queuedValue = null
          await response.text()
          this.#request({ [this.fieldValue]: queued })
          return
        }

        Turbo.renderStreamMessage(await response.text())
        this.#pending = false
      })
      .catch(() => {
        this.#pending = false
        this.#queuedValue = null
        this.#showError(this.requestFailedValue)
      })
      .finally(() => {
        clearTimeout(timeoutId)
        if (!this.#pending) this.#mutex?.removeAttribute('aria-busy')
      })
  }

  #rejectInput () {
    this.inputTarget.value = this.#acceptedValue
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.#showError(this.invalidValueValue)
  }

  #mutexNode () {
    return this.element.closest('[data-decomposition-mutex]')
  }

  #showError (message) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.add(...this.errorClasses)
  }

  #clearStatus () {
    this.statusTarget.textContent = ''
    this.statusTarget.classList.remove(...this.errorClasses)
  }
}
